/**
 * Created by Jerker Nord on 2024-01-08.
 */
import {StageLayer} from "@nolimitcity/slot-launcher/bin/StageLayer";
import {LonerTopDuckAnimator} from "./LonerTopDuckAnimator";
import {Loner} from "../../../Loner";
import {LonerGameAssets} from "../../../LonerGameAssets";
import {LonerMiddleDuckAnimator} from "./LonerMiddleDuckAnimator";
import {LonerBottomDuckAnimator} from "./LonerBottomDuckAnimator";
import {gsap} from "gsap";
import {LonerSSDAmmo} from "./LonerSSDAmmo";
import {LonerSSDMultiplier} from "./LonerSSDMultiplier";
import {MathHelper} from "@nolimitcity/slot-game/bin/utils/MathHelper";
import {LonerSound} from "../../../sound/LonerSound";
import {LonerDuckMX2Symbol} from "./symbols/LonerDuckMX2Symbol";
import {NolimitApplication} from "@nolimitcity/slot-launcher/bin/NolimitApplication";
import {LonerSpine} from "../../../components/LonerSpine";
import {LonerDuckClipSymbol} from "./symbols/LonerDuckClipSymbol";
import {LonerDuckMX1Symbol} from "./symbols/LonerDuckMX1Symbol";
import {LonerDuckMaxWinSymbol} from "./symbols/LonerDuckMaxWinSymbol";
import {SpineUtils} from "../../../utils/SpineUtils";

enum MX1_EXPLOSION_ANIMATION_NAMES {
    explosion1 = "explosion_1",
    explosion2 = "explosion_2"
}

export class LonerSSDMainView extends PIXI.Container implements StageLayer {

    public static NO_MULTIPLIER_DUCKS:string[] = ["MX1", "X", "CLIP", "MAX", "BOMB"];
    public static CROSSHAIR_BASE_POSITIONS_TOP:PIXI.Point[] = [new PIXI.Point(70, 310), new PIXI.Point(360, 310), new PIXI.Point(630, 310)];
    public static CROSSHAIR_BASE_POSITIONS_MIDDLE:PIXI.Point[] = [new PIXI.Point(80, 410), new PIXI.Point(340, 410), new PIXI.Point(620, 410)];
    public static CROSSHAIR_BASE_POSITIONS_BOTTOM:PIXI.Point[] = [new PIXI.Point(75, 510), new PIXI.Point(380, 510), new PIXI.Point(635, 510)];
    public static CROSSHAIR_MISS_POSITIONS_TOP:PIXI.Point[] = [new PIXI.Point(215, 310), new PIXI.Point(495, 310)];
    public static CROSSHAIR_MISS_POSITIONS_MIDDLE:PIXI.Point[] = [new PIXI.Point(210, 410), new PIXI.Point(485, 410)];
    public static CROSSHAIR_MISS_POSITIONS_BOTTOM:PIXI.Point[] = [new PIXI.Point(215, 510), new PIXI.Point(510, 510)];
    public static DUCK_START_DELAY:number = 0.6;

    private static TOP_ANIMATOR_POSITION:PIXI.Point = new PIXI.Point(350, 180);
    private static MIDDLE_ANIMATOR_POSITION:PIXI.Point = new PIXI.Point(400, 410);
    private static BOTTOM_ANIMATOR_POSITION:PIXI.Point = new PIXI.Point(360, 510);
    private static AMMO_VIEW_POSITION:PIXI.Point = new PIXI.Point(540, 140);
    private static MULTIPLIER_POSITION:PIXI.Point = new PIXI.Point(350, 170);
    private static CROSSHAIR_START_POSITION:PIXI.Point = new PIXI.Point(360, 360);

    private _foregroundFS:PIXI.Sprite;
    private _topDuckAnimator:LonerTopDuckAnimator;
    private _middleDuckAnimator:LonerMiddleDuckAnimator;
    private _bottomDuckAnimator:LonerBottomDuckAnimator;
    private _hideTL:gsap.core.Timeline;
    private _showTL:gsap.core.Timeline;
    private _ammoView:LonerSSDAmmo;
    private _multiplierView:LonerSSDMultiplier;
    private _crossHair:PIXI.Sprite;
    private _duckAnimatorsComplete:boolean[];
    private _lastSoundIndexOfDuckShoot:number = 0;
    private _shootDuckTL:gsap.core.Timeline;
    private _moveCrosshairTL:gsap.core.Timeline;
    private _startBulletTimeTL:gsap.core.Timeline;
    private _stopBulletTimeTL:gsap.core.Timeline;
    private _mx2UpdateCounter:number = 0;
    private _allMX2Symbols:LonerDuckMX2Symbol[][] = [[], [], []];
    private _background:PIXI.Sprite;
    private _bulletHolesUsed:boolean[];
    private _bulletHoleTexturesUsed:boolean[];
    private _mx1ExplosionSpine:LonerSpine;
    private _stoppingDucks:boolean = false;
    private _animatorTimeLeft:number;
    private _betSentTime:number;
    private _animatorsComplete:boolean[] = [false, false, false];
    private _shootCompleteCallback:Function;
    private _shotServerResponse:boolean = false;
    private _ducksReadyForShot:boolean = false;
    private _ducksWaiting:boolean = false;
    private _missTextureIndex:number;
    private _missPositionIndex:number;
    private _missPosition:PIXI.Point;
    private _mx2ToUpdate:LonerDuckMX2Symbol[];
    private _mx2UpdateIndex:number;
    private _mx2BetweenUpdateDelay:number;

    public bulletTime:any = {
        timescale : 1
    }

    constructor() {
        super();
        this.name = "LonerSSDMainView";
        this.init();
    }

    private init():void {
        this.alpha = 0;
        this.visible = false;
        this._duckAnimatorsComplete = [false, false, false];
        this.resetBulletHolesUsed();
        this.resetBulletHoleTexturesUsed();

        this._topDuckAnimator = new LonerTopDuckAnimator();
        this._topDuckAnimator.position = LonerSSDMainView.TOP_ANIMATOR_POSITION;
        this.addChild(this._topDuckAnimator);

        this._middleDuckAnimator = new LonerMiddleDuckAnimator();
        this._middleDuckAnimator.position = LonerSSDMainView.MIDDLE_ANIMATOR_POSITION;
        this.addChild(this._middleDuckAnimator);

        this._bottomDuckAnimator = new LonerBottomDuckAnimator();
        this._bottomDuckAnimator.position = LonerSSDMainView.BOTTOM_ANIMATOR_POSITION;
        this.addChild(this._bottomDuckAnimator);

        this._multiplierView = new LonerSSDMultiplier();
        this._multiplierView.position = LonerSSDMainView.MULTIPLIER_POSITION;
        this.addChild(this._multiplierView);

        this._foregroundFS = new PIXI.Sprite(Loner.assetLoader.getTextures(LonerGameAssets.SSD_FOREGROUND)[0]);
        this._foregroundFS.anchor.set(0.5, 0.5);
        this._foregroundFS.position.set(360, 610);
        this.addChild(this._foregroundFS);

        this._mx1ExplosionSpine = new LonerSpine(Loner.assetLoader.getSpineAsset(LonerGameAssets.MX1_EXPLOSION_SPINE));
        this._mx1ExplosionSpine.position.set(360, 360);
        this._mx1ExplosionSpine.visible = false;
        this.addChild(this._mx1ExplosionSpine);

        this._ammoView = new LonerSSDAmmo();
        this._ammoView.position = LonerSSDMainView.AMMO_VIEW_POSITION;
        this.addChild(this._ammoView);

        this._crossHair = new PIXI.Sprite(Loner.assetLoader.getTextures(LonerGameAssets.SSD_CROSSHAIR)[0]);
        this._crossHair.anchor.set(0.5, 0.5);
        this._crossHair.position = LonerSSDMainView.CROSSHAIR_START_POSITION;
        this.addChild(this._crossHair);

        this._showTL = gsap.timeline({paused : true});
        this._showTL.add(() => {
            this.visible = true;
        });
        this._showTL.add(gsap.to(this, {duration : 0.2, alpha : 1}));

        this._hideTL = gsap.timeline({paused : true});
        this._hideTL.add(gsap.to(this, {duration : 0.2, alpha : 0}));
        this._hideTL.add(() => {
            this.visible = false;
        });

        this._background = new PIXI.Sprite(PIXI.Texture.WHITE);
        this._background.tint = 0x000000;
        this._background.alpha = 0;
        this.addChild(this._background);

        this.onResize();
    }

    public playMX1Explosion(reel:number, row:number):void {
        const mx1ExplosionTL:gsap.core.Timeline = gsap.timeline();
        mx1ExplosionTL.add(() => {
            if(row == 0) {
                this._mx1ExplosionSpine.position.x = LonerSSDMainView.CROSSHAIR_BASE_POSITIONS_TOP[reel].x;
                this._mx1ExplosionSpine.position.y = LonerSSDMainView.CROSSHAIR_BASE_POSITIONS_TOP[reel].y;
            } else if(row == 1) {
                this._mx1ExplosionSpine.position.x = LonerSSDMainView.CROSSHAIR_BASE_POSITIONS_TOP[reel - 2].x;
                this._mx1ExplosionSpine.position.y = LonerSSDMainView.CROSSHAIR_BASE_POSITIONS_TOP[reel - 2].y;
            } else if(row == 2) {
                this._mx1ExplosionSpine.position.x = LonerSSDMainView.CROSSHAIR_BASE_POSITIONS_BOTTOM[reel].x;
                this._mx1ExplosionSpine.position.y = LonerSSDMainView.CROSSHAIR_BASE_POSITIONS_BOTTOM[reel].y;
            }
            this._mx1ExplosionSpine.visible = true;
            SpineUtils.resetSpineAnimation(this._mx1ExplosionSpine);
        });
        mx1ExplosionTL.add(() => {
            if(MathHelper.randomIntInRange(0, 1) == 0) {
                this._mx1ExplosionSpine.state.setAnimation(0, MX1_EXPLOSION_ANIMATION_NAMES.explosion1, false);
            } else {
                this._mx1ExplosionSpine.state.setAnimation(0, MX1_EXPLOSION_ANIMATION_NAMES.explosion2, false);
            }
        })
        mx1ExplosionTL.add(gsap.timeline({delay : 0.03}));
        mx1ExplosionTL.add(gsap.to(this._mx1ExplosionSpine.position, {
            ease : "none",
            duration : 0.13,
            y : 360,
            x : 360
        }));
        mx1ExplosionTL.add(() => {Loner.shake(1.1, 200, 3.0)});
    }

    private playScreenShakeAnimation(symName:string):gsap.core.Timeline {
        const tl:gsap.core.Timeline = gsap.timeline();
        if(symName !== "X") {
            if(symName == "MAX") {
                Loner.shake(0.5, 400, 4.5);
            } else if(symName == "MX2") {
                Loner.shake(0.2, 120, 2.5);
            } else if(symName == "MX1") {
                //Handled by symbol
            } else {
                Loner.shake(0.1, 80, 2);
            }
        }
        return tl;
    }

    public initAnimators():void {
        this._topDuckAnimator.initDucks(Loner.model.duckReels[0], Loner.model.duckValues[0]);
        this._middleDuckAnimator.initDucks(Loner.model.duckReels[1], Loner.model.duckValues[1]);
        this._bottomDuckAnimator.initDucks(Loner.model.duckReels[2], Loner.model.duckValues[2]);
    }

    public setAllDucksToBurnt(noBurntReel:number, noBurntRow:number):void {
        for(let i:number = 0; i < this._topDuckAnimator.ducks.length - 1; i++) {
            if(this._topDuckAnimator.ducks[i].row == noBurntRow && this._topDuckAnimator.ducks[i].reel == noBurntReel) {
                //Do something?
            } else {
                if(this._topDuckAnimator.ducks[i].symbolName == "CLIP") {
                    (<LonerDuckClipSymbol>this._topDuckAnimator.ducks[i]).getSymbolBurntTL();
                } else if(this._topDuckAnimator.ducks[i].symbolName == "MX1") {
                    (<LonerDuckMX1Symbol>this._topDuckAnimator.ducks[i]).getSymbolBurntTL();
                } else if(this._topDuckAnimator.ducks[i].symbolName == "MX2") {
                    (<LonerDuckMX2Symbol>this._topDuckAnimator.ducks[i]).getSymbolBurntTL();
                } else if(this._topDuckAnimator.ducks[i].symbolName == "MAX") {
                    (<LonerDuckMaxWinSymbol>this._topDuckAnimator.ducks[i]).getSymbolBurntTL();
                } else {
                    this._topDuckAnimator.ducks[i].getSymbolBurntTL();
                }
            }
        }
        for(let i:number = 0; i < this._bottomDuckAnimator.ducks.length - 1; i++) {
            if(this._bottomDuckAnimator.ducks[i].row == noBurntRow && this._bottomDuckAnimator.ducks[i].reel == noBurntReel) {
                //Do something?
            } else {
                if(this._bottomDuckAnimator.ducks[i].symbolName == "CLIP") {
                    (<LonerDuckClipSymbol>this._bottomDuckAnimator.ducks[i]).getSymbolBurntTL();
                } else if(this._bottomDuckAnimator.ducks[i].symbolName == "MX1") {
                    (<LonerDuckMX1Symbol>this._bottomDuckAnimator.ducks[i]).getSymbolBurntTL();
                } else if(this._bottomDuckAnimator.ducks[i].symbolName == "MX2") {
                    (<LonerDuckMX2Symbol>this._bottomDuckAnimator.ducks[i]).getSymbolBurntTL();
                } else if(this._bottomDuckAnimator.ducks[i].symbolName == "MAX") {
                    (<LonerDuckMaxWinSymbol>this._bottomDuckAnimator.ducks[i]).getSymbolBurntTL();
                } else {
                    this._bottomDuckAnimator.ducks[i].getSymbolBurntTL();
                }
            }
        }
        for(let i:number = 1; i < this._middleDuckAnimator.ducks.length; i++) {
            if(this._middleDuckAnimator.ducks[i].row == noBurntRow && this._middleDuckAnimator.ducks[i].reel == noBurntReel) {
                //Do something?
            } else {
                if(this._middleDuckAnimator.ducks[i].symbolName == "CLIP") {
                    (<LonerDuckClipSymbol>this._middleDuckAnimator.ducks[i]).getSymbolBurntTL();
                } else if(this._middleDuckAnimator.ducks[i].symbolName == "MX1") {
                    (<LonerDuckMX1Symbol>this._middleDuckAnimator.ducks[i]).getSymbolBurntTL();
                } else if(this._middleDuckAnimator.ducks[i].symbolName == "MX2") {
                    (<LonerDuckMX2Symbol>this._middleDuckAnimator.ducks[i]).getSymbolBurntTL()
                } else if(this._middleDuckAnimator.ducks[i].symbolName == "MAX") {
                    (<LonerDuckMaxWinSymbol>this._middleDuckAnimator.ducks[i]).getSymbolBurntTL();
                } else {
                    this._middleDuckAnimator.ducks[i].getSymbolBurntTL();
                }
            }
        }
    }

    public doMX2Update(symbols:LonerDuckMX2Symbol[], animatorIndex:number):void {
        this._allMX2Symbols[animatorIndex] = symbols;
        this._mx2UpdateCounter++;
        this._mx2ToUpdate = [];
        this._mx2UpdateIndex = 0;
        if(this._mx2UpdateCounter == 3) {
            const noOfMX2Syms:number = this._allMX2Symbols[0].length + this._allMX2Symbols[1].length + this._allMX2Symbols[2].length;
            if(noOfMX2Syms > 0) {
                for(let i:number = 0; i < this._allMX2Symbols.length; i++) {
                    for(let j:number = 0; j < this._allMX2Symbols[i].length; j++) {
                        if((this._allMX2Symbols[i][j].row == 1 && this._allMX2Symbols[i][j].reel < 4) ||
                            ((this._allMX2Symbols[i][j].row == 0 || this._allMX2Symbols[i][j].row == 2) && this._allMX2Symbols[i][j].reel > 0)
                        ) {
                            this._mx2ToUpdate.push(this._allMX2Symbols[i][j]);
                        }
                    }
                }
                if(this._mx2ToUpdate.length == 1) {
                    this._mx2BetweenUpdateDelay = 0
                } else {
                    this._mx2BetweenUpdateDelay = 0.2 / (this._mx2ToUpdate.length - 1);
                }

                this.playMXUpdate();
            } else {
                this._mx2UpdateCounter = 0;
            }
        }
    }

    private playMXUpdate():void {
        if(this._mx2UpdateIndex == this._mx2ToUpdate.length) {
            //All MX2 updated
            this.onAllMX2Updated();
        } else {
            const mx2TL:gsap.core.Timeline = gsap.timeline({onComplete : () => this.playMXUpdate()});
            mx2TL.add(()=>{this._mx2ToUpdate[this._mx2UpdateIndex].multiplierValue = this.collectAllDuckValues()})
            mx2TL.add(() => this._mx2ToUpdate[this._mx2UpdateIndex].startAttentionAnimation());
            mx2TL.add(() => {
                Loner.soundPlayer.playSound(LonerSound.SSD_VAN_MULTI_UPDATE);
            });
            mx2TL.add(() => {this._mx2ToUpdate[this._mx2UpdateIndex].updateSymbolValue()});
            mx2TL.add(gsap.timeline({delay : this._mx2BetweenUpdateDelay}));
            mx2TL.add(() => this._mx2ToUpdate[this._mx2UpdateIndex].stopAttentionAnimation());
            mx2TL.add(() => {
                this._mx2UpdateIndex++;
            });
        }
    }

    private collectAllDuckValues():number {
        //Goes through all visible ducks and adds all values except the one sent in
        let newMX2Value:number = 0;
        for(let i:number = 0; i < this._topDuckAnimator.ducks.length; i++) {
            //console.log("this._topDuckAnimator.ducks[i].reel: " + this._topDuckAnimator.ducks[i].reel);
            if(this._topDuckAnimator.ducks[i].multiplierValue > 0 && this._topDuckAnimator.ducks[i].reel > 0) {
                newMX2Value += this._topDuckAnimator.ducks[i].multiplierValue;
            }
        }

        for(let i:number = 0; i < this._middleDuckAnimator.ducks.length; i++) {
            if(this._middleDuckAnimator.ducks[i].multiplierValue > 0 && this._middleDuckAnimator.ducks[i].reel < 4) {
                //console.log("Middle value is: " + this._middleDuckAnimator.ducks[i].multiplierValue);
                newMX2Value += this._middleDuckAnimator.ducks[i].multiplierValue;
            }

        }
        for(let i:number = 0; i < this._bottomDuckAnimator.ducks.length; i++) {
            if(this._bottomDuckAnimator.ducks[i].multiplierValue > 0 && this._bottomDuckAnimator.ducks[i].reel > 0) {
                //console.log("Bottom value is: " + this._bottomDuckAnimator.ducks[i].multiplierValue);
                newMX2Value += this._bottomDuckAnimator.ducks[i].multiplierValue;
            }
        }

        return newMX2Value;
    }

    private onAllMX2Updated():void {
        this._mx2UpdateCounter = 0;
    }

    public startBulletTime(slowDownDuration:number, newSpeed:number):gsap.core.Timeline {
        this._startBulletTimeTL = gsap.timeline();
        this._startBulletTimeTL.add(gsap.to(this.bulletTime, {duration : slowDownDuration, timescale : newSpeed}));

        return this._startBulletTimeTL;
    }

    public stopBulletTime(speedUpDuration:number):gsap.core.Timeline {
        this._stopBulletTimeTL = gsap.timeline();
        this._stopBulletTimeTL.add(gsap.to(this.bulletTime, {duration : speedUpDuration, timescale : 1}));

        return this._stopBulletTimeTL;
    }

    public getDuckValuePath(duckValue:number, duck:string):string {
        if(duck.indexOf("MX1") > -1) {
            return "MX1/";
        }
        if(duck.indexOf("MX2") > -1) {
            return "MX2/";
        }
        if(duck.indexOf("CLIP") > -1) {
            return "CLIP/";
        }
        if(duck.indexOf("MAX") > -1) {
            return "MAX/";
        }
        if(duckValue > 15) {
            return "whiteDucks/";
        } else if(duckValue > 5) {
            return "yellowDucks/";
        } else {
            return "brownDucks/";
        }
    }

    private onShootDuckTLUpdate():void {
        this._shootDuckTL.timeScale(this.getTimescale());
    }

    public playShootDuckTL(shootCompleteCallback:Function):void {
        this._allMX2Symbols = [];
        this._shootCompleteCallback = shootCompleteCallback;
        this._shotServerResponse = true;
        this.checkReadyToShoot();

        let reelPosition:number = Loner.model.shotData.position[0];
        let rowPosition:number = Loner.model.shotData.position[1];
        let symbolShotName:string = Loner.model.shotData.symbolShot;

        const ms:number = 2500 - (performance.now() - this._betSentTime);
        this._animatorTimeLeft = (Math.round((ms + Number.EPSILON)) / 1000);
        if(this._animatorTimeLeft < 0) {
            this._animatorTimeLeft = 0.05;
        }

        this._moveCrosshairTL = gsap.timeline();
        if(symbolShotName !== "X") {
            if(rowPosition == 0) {
                //TOP
                this._moveCrosshairTL.add(gsap.to(this._crossHair.position, {
                    duration : this._animatorTimeLeft,
                    x : LonerSSDMainView.CROSSHAIR_BASE_POSITIONS_TOP[reelPosition - 1].x,
                    y : LonerSSDMainView.CROSSHAIR_BASE_POSITIONS_TOP[reelPosition - 1].y
                }));
            } else if(rowPosition == 1) {
                //MIDDLE
                this._moveCrosshairTL.add(gsap.to(this._crossHair.position,
                    {
                        duration : this._animatorTimeLeft,
                        x : LonerSSDMainView.CROSSHAIR_BASE_POSITIONS_MIDDLE[reelPosition - 1].x,
                        y : LonerSSDMainView.CROSSHAIR_BASE_POSITIONS_MIDDLE[reelPosition - 1].y
                    }));
            } else if(rowPosition == 2) {
                //BOTTOM
                this._moveCrosshairTL.add(gsap.to(this._crossHair.position,
                    {
                        duration : this._animatorTimeLeft,
                        x : LonerSSDMainView.CROSSHAIR_BASE_POSITIONS_BOTTOM[reelPosition - 1].x,
                        y : LonerSSDMainView.CROSSHAIR_BASE_POSITIONS_BOTTOM[reelPosition - 1].y
                    }));
            } else {
                //debugger;
            }
        } else {
            if(this._bulletHolesUsed.includes(false) == false) {
                this.resetBulletHolesUsed();
            }
            this._missPositionIndex = MathHelper.randomIntInRange(0, this._bulletHolesUsed.length - 1);
            while(this._bulletHolesUsed[this._missPositionIndex] == true) {
                this._missPositionIndex = MathHelper.randomIntInRange(0, this._bulletHolesUsed.length - 1);
            }
            this._bulletHolesUsed[this._missPositionIndex] = true;

            if(this._bulletHoleTexturesUsed.includes(false) == false) {
                this.resetBulletHoleTexturesUsed()
            }
            this._missTextureIndex = MathHelper.randomIntInRange(0, LonerGameAssets.SSD_BULLET_HOLES.length - 1);
            while(this._bulletHoleTexturesUsed[this._missTextureIndex] == true) {
                this._missTextureIndex = MathHelper.randomIntInRange(0, LonerGameAssets.SSD_BULLET_HOLES.length - 1);
            }
            this._bulletHolesUsed[this._missPositionIndex] = true;
            if(this._missPositionIndex > 3) {
                this._missPosition = LonerSSDMainView.CROSSHAIR_MISS_POSITIONS_BOTTOM[this._missPositionIndex - 4];
                this._moveCrosshairTL.add(gsap.to(this._crossHair.position, {
                    duration : this._animatorTimeLeft,
                    x : this._missPosition.x,
                    y : this._missPosition.y
                }));
            } else if(this._missPositionIndex > 1) {
                this._missPosition = LonerSSDMainView.CROSSHAIR_MISS_POSITIONS_MIDDLE[this._missPositionIndex - 2];
                this._moveCrosshairTL.add(gsap.to(this._crossHair.position, {
                    duration : this._animatorTimeLeft,
                    x : this._missPosition.x,
                    y : this._missPosition.y
                }));
            } else {
                this._missPosition = LonerSSDMainView.CROSSHAIR_MISS_POSITIONS_TOP[this._missPositionIndex];
                this._moveCrosshairTL.add(gsap.to(this._crossHair.position, {
                    duration : this._animatorTimeLeft,
                    x : this._missPosition.x,
                    y : this._missPosition.y
                }));
            }
        }
    }

    private newDoShoot():void {
        let symbolName:string = Loner.model.shotData.symbolShot;
        let reelPosition:number = Loner.model.shotData.position[0];
        let rowPosition:number = Loner.model.shotData.position[1];
        let symbolShotName:string = Loner.model.shotData.symbolShot;
        let awardMultiplier:number = Loner.model.shotData.awardMultiplier;

        if(this._shootDuckTL) {
            this._shootDuckTL.kill();
        }

        this._shootDuckTL = gsap.timeline({onUpdate : () => this.onShootDuckTLUpdate()});

        if(symbolShotName !== "X") {
            if(rowPosition == 0) {
                this._shootDuckTL.add(() => {
                    this.playScreenShakeAnimation(symbolName);
                    this.playDuckShootSound(symbolName);
                    this._topDuckAnimator.shootDuck(reelPosition, symbolShotName)
                });
            } else if(rowPosition == 1) {
                //MIDDLE
                this._shootDuckTL.add(() => {
                    this.playScreenShakeAnimation(symbolName);
                    this.playDuckShootSound(symbolName);
                    this._middleDuckAnimator.shootDuck(reelPosition, symbolShotName)
                });
            } else if(rowPosition == 2) {
                //BOTTOM
                this._shootDuckTL.add(() => {
                    this.playScreenShakeAnimation(symbolName);
                    this.playDuckShootSound(symbolName);
                    this._bottomDuckAnimator.shootDuck(reelPosition, symbolShotName)
                });
            } else {
                //debugger;
            }
        } else {
            if(this._missPositionIndex > 3) {
                this._shootDuckTL.add(():void => {
                    this._bottomDuckAnimator.missDuck(this._missPositionIndex - 4, LonerGameAssets.SSD_BULLET_HOLES[this._missTextureIndex]);
                    this.playDuckShootSound(symbolName);
                    this._crossHair.position = this._missPosition;
                });
            } else if(this._missPositionIndex > 1) {
                this._shootDuckTL.add(():void => {
                    this._middleDuckAnimator.missDuck(this._missPositionIndex - 2, LonerGameAssets.SSD_BULLET_HOLES[this._missTextureIndex]);
                    this.playDuckShootSound(symbolName);
                    this._crossHair.position = this._missPosition;
                });
            } else {
                this._shootDuckTL.add(():void => {
                    this._topDuckAnimator.missDuck(this._missPositionIndex, LonerGameAssets.SSD_BULLET_HOLES[this._missTextureIndex]);
                    this.playDuckShootSound(symbolName);
                    this._crossHair.position = this._missPosition;
                });
            }
        }

        if(symbolName == "CLIP") {
            this._shootDuckTL.add(() => {
                this._ammoView.setAmmo(5);
            });
        } else {
            this._shootDuckTL.add(() => {
                this._ammoView.decreaseAmmo();
                this._multiplierView.addToMultiplier(awardMultiplier);
            });
        }

        this._shootDuckTL.add(() => {
            this._shootCompleteCallback()
        });

    }

    private checkReadyToShoot():void {
        if(this._shotServerResponse == true && this._ducksReadyForShot == true) {
            this._shotServerResponse = false;
            this._ducksReadyForShot = false;
            this.newDoShoot();
            if(this._ducksWaiting == true) {
                this._ducksWaiting = false;
                this._topDuckAnimator.resumeDuckAnimation();
                this._middleDuckAnimator.resumeDuckAnimation();
                this._bottomDuckAnimator.resumeDuckAnimation();
            }
        }
    }

    public duckAnimatorCycleComplete(animatorIndex:number):void {
        this._animatorsComplete[animatorIndex] = true;
        if(this._animatorsComplete.includes(false) == false) {
            this._ducksReadyForShot = true;
            this._animatorsComplete[0] = false;
            this._animatorsComplete[1] = false;
            this._animatorsComplete[2] = false;
            if(this._shotServerResponse == false) {
                this._topDuckAnimator.pauseDuckAnimation();
                this._middleDuckAnimator.pauseDuckAnimation();
                this._bottomDuckAnimator.pauseDuckAnimation();
                this._ducksWaiting = true;
            }
            this.checkReadyToShoot();
        }
    }

    private resetBulletHolesUsed():void {
        this._bulletHolesUsed = [false, false, false, false, false, false];
    }

    private resetBulletHoleTexturesUsed():void {
        this._bulletHoleTexturesUsed = [false, false, false, false];
    }

    public stopAllDucks():gsap.core.Timeline {
        this._stoppingDucks = true;
        const stopDucksTL:gsap.core.Timeline = gsap.timeline();
        stopDucksTL.add(gsap.timeline({delay : 1}));
        stopDucksTL.add(() => {this.startBulletTime(2.5, 0.0001)});
        stopDucksTL.add(gsap.timeline({delay : 0.5}));
        stopDucksTL.add([
            gsap.to(this._background, {duration : 1.5, alpha : 1}),
            () => {Loner.soundPlayer.fadeMusic(1, 0, 1500)}
        ]);
        stopDucksTL.add(() => {Loner.soundPlayer.stopSound(LonerSound.SDD_LOOP)}, "-=1");
        stopDucksTL.add(this.stopBulletTime(0.001));
        stopDucksTL.add(() => {
            this._topDuckAnimator.stopDuckAnimation();
            this._middleDuckAnimator.stopDuckAnimation();
            this._bottomDuckAnimator.stopDuckAnimation();
        });
        stopDucksTL.add(() => {
            this.reset()
        });
        return stopDucksTL;
    }

    public stopAllDucksMaxWin():void {
        this._topDuckAnimator.stopDuckAnimation();
        this._middleDuckAnimator.stopDuckAnimation();
        this._bottomDuckAnimator.stopDuckAnimation();
        this.reset()
    }

    public startTheDucks():gsap.core.Timeline {
        const startDucksTL:gsap.core.Timeline = gsap.timeline();
        startDucksTL.add(() => {
            this.bulletTime.timescale = 1;
            this._background.alpha = 0;
        })
        startDucksTL.add(gsap.timeline({delay : LonerSSDMainView.DUCK_START_DELAY}));
        startDucksTL.add(() => {
            Loner.soundPlayer.playSound(LonerSound.SDD_LOOP, 1, Number.MAX_VALUE);
        });
        startDucksTL.add(() => {
            this._middleDuckAnimator.getMoveAllDucksTL();
            this._topDuckAnimator.getMoveAllDucksTL();
            this._bottomDuckAnimator.getMoveAllDucksTL();
        });

        return startDucksTL;
    }

    public restore():void {
        this.startTheDucks();
        this._ammoView.setAmmo(Loner.model.freespinsLeft);
        this._multiplierView.setMultiplierValue(Loner.model.totalBonusWin);
    }

    public hide():void {
        if(this._showTL.isActive()) {
            this._showTL.pause();
        }
        this._hideTL.restart();
    }

    public show():void {
        if(this._hideTL.isActive()) {
            this._hideTL.pause();
        }
        this._showTL.restart();
    }

    private playDuckShootSound(symName:string):void {
        if(symName == "MX1") {
            Loner.soundPlayer.playSound(LonerSound.SSD_NORMAL_DUCK_SHOOT_SOUNDS[1]);
            Loner.soundPlayer.playSound(LonerSound.SSD_BOMB_SHOOT_SOUND);
        } else if(symName == "X") {
            Loner.soundPlayer.playSound(LonerSound.SSD_MISS_DUCK_SHOOT_SOUNDS.next());
        } else if(symName == "CLIP") {
            Loner.soundPlayer.playSound(LonerSound.SSD_AMMO_SHOOT_SOUND);
        } else if(symName == "MX2") {
            Loner.soundPlayer.playSound(LonerSound.SSD_VAN_SHOOT_SOUND);
        } else if(symName === "MAX") {
            Loner.soundPlayer.playSound(LonerSound.SSD_MAX_SYM_SHOOT);
        } else {
            let soundIdx:number = getSoundIndex();
            while(this._lastSoundIndexOfDuckShoot == soundIdx) {
                soundIdx = getSoundIndex();
            }
            Loner.soundPlayer.playSound(LonerSound.SSD_NORMAL_DUCK_SHOOT_SOUNDS[soundIdx]);
            this._lastSoundIndexOfDuckShoot = soundIdx;

            function getSoundIndex():number {
                return Math.floor(Math.random() * LonerSound.SSD_NORMAL_DUCK_SHOOT_SOUNDS.length);
            }
        }
    }

    public onResize():void {
        this._background.width = NolimitApplication.screenBounds.width;
        this._background.height = NolimitApplication.screenBounds.height;
        this._background.x = NolimitApplication.screenBounds.left;
        this._background.y = NolimitApplication.screenBounds.top;
    }

    public getTimescale():number {
        return <number>this.bulletTime.timescale;
    }

    public betSentTime(time:number):void {
        this._betSentTime = time;
    }

    public reset():void {
        this.hide();
        this._topDuckAnimator.reset();
        this._topDuckAnimator.position = LonerSSDMainView.TOP_ANIMATOR_POSITION;
        this._middleDuckAnimator.reset();
        this._middleDuckAnimator.position = LonerSSDMainView.MIDDLE_ANIMATOR_POSITION;
        this._bottomDuckAnimator.reset();
        this._bottomDuckAnimator.position = LonerSSDMainView.BOTTOM_ANIMATOR_POSITION;
        this._ammoView.reset();
        this._multiplierView.reset();
        this._crossHair.position = LonerSSDMainView.CROSSHAIR_START_POSITION;
        this.visible = false;
        this.resetBulletHolesUsed();
        this.resetBulletHoleTexturesUsed();
        this._stoppingDucks = false;
    }
}